import React, { useState, useMemo } from 'react';
import { Head } from '@inertiajs/react';
import { AlertTriangle, Plus, Search, X } from 'lucide-react';
import { EmptyState, FilterEmptyState } from '@/frontend/components/EmptyState';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { router } from '@inertiajs/react';
import { CredentialsList } from '@/frontend/components/Credentials/CredentialsList';
import ConnectCredentialModal from '@/frontend/components/Credentials/ConnectCredentialModal';
import CredentialFormModal from '@/frontend/components/Credentials/CredentialFormModal';
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell';
import OrganizationConnectionsNav from '@/frontend/layouts/worker-first/OrganizationConnectionsNav';
import { usePermissions } from '@/frontend/hooks/usePermissions';
import DashboardLayout from '@/frontend/layouts/DashboardLayout';
import type { ScopeWorker } from '@/frontend/types/inertia';
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types';
import type { Credential } from '@/frontend/pages/Dashboard/Integrations/constants';

interface Props {
  page_title: string;
  credentials: Credential[];
  scope_worker?: ScopeWorker;
  // Ficha completa do agente em agent-scope: alimenta o FichaHeader dentro do
  // WorkerPortalShell (a moldura do portal). Ausente no modo org-level.
  scope_worker_header?: DigitalWorkerDetail;
  attachable_credentials?: Array<{ id: string; name: string }>;
}

const CredentialsIndex = ({ page_title, credentials, scope_worker, scope_worker_header, attachable_credentials = [] }: Props) => {
  const { can_manage_resources } = usePermissions();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Primeiro nome do agente para a copy do portal ("Credenciais que a Luna usa").
  const workerFirstName = scope_worker ? scope_worker.name.split(' ')[0] : '';

  const hasActiveFilters = search.trim() !== '';

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return credentials;
    return credentials.filter((c) => c.name.toLowerCase().includes(q));
  }, [credentials, search]);

  const clearFilters = () => {
    setSearch('');
  };

  const body = (
    <>
        {!scope_worker && <OrganizationConnectionsNav current="credentials" />}

        {/* Painel do agente: header é o LUGAR DE CONECTAR (título "que a {nome}
            usa" + ação única "Conectar credencial"), não uma lista de admin.
            Org-level (sidebar) segue com a copy e o layout de antes. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            {/* Título de página do Painel: 19/650 (escala do DS). */}
            <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">
              {scope_worker ? `Credenciais que ${workerFirstName} usa` : page_title}
            </h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              {scope_worker
                ? `Segredos e chaves que ${workerFirstName} precisa para trabalhar.`
                : 'Chaves de API e tokens de acesso para seus agentes.'}
            </p>
          </div>

          {scope_worker && can_manage_resources && (
            <Button size="sm" onClick={() => setConnectModalOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Conectar credencial
            </Button>
          )}
          {!scope_worker && can_manage_resources && (
            <Button size="sm" onClick={() => setCreateModalOpen(true)}>
              <Plus className="h-3.5 w-3.5" />
              Nova credencial
            </Button>
          )}
        </div>

        {!can_manage_resources && (
          <Alert variant="warning">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Você não tem permissão para gerenciar credenciais.</AlertTitle>
            <AlertDescription>Você pode visualizar as credenciais, mas não pode modificá-las.</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar credenciais…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" aria-label="Buscar credenciais" />
          </div>
          <span className="text-[13px] tabular-nums text-muted-foreground">{filtered.length} {filtered.length === 1 ? 'credencial' : 'credenciais'}</span>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="h-3 w-3" /> Limpar filtros
            </Button>
          )}
        </div>

        {filtered.length > 0 && (
          <CredentialsList
            credentials={filtered}
            onDetach={scope_worker && can_manage_resources ? (credential) => {
              router.post(`/dashboard/digital_workers/${scope_worker.id}/toggle_credential`, {
                credential_id: credential.id,
                enabled: false,
              }, { preserveScroll: true });
            } : undefined}
          />
        )}

        {/* Busca sem resultado (tipo B): spot central com eco do termo. */}
        {filtered.length === 0 && hasActiveFilters && (
          <FilterEmptyState query={search} onClear={clearFilters} clearLabel="Limpar busca" />
        )}

        {/* Vazio do portal com AÇÃO (não beco sem saída): conecta uma existente
            ou cria uma nova, pela mesma ação do header. */}
        {scope_worker && credentials.length === 0 && !hasActiveFilters && (
          <EmptyState
            illustration="credentials"
            title="Cofre vazio (por enquanto)"
            description={`${workerFirstName} ainda não usa nenhuma credencial. Conecte uma existente da organização ou crie uma nova.`}
            action={can_manage_resources ? (
              <Button size="sm" onClick={() => setConnectModalOpen(true)}>
                <Plus className="h-3.5 w-3.5" />
                Conectar credencial
              </Button>
            ) : undefined}
          />
        )}
    </>
  );

  return (
    <>
      <Head title={page_title} />
      {/* Agent-scope: SEM px-4 py-6 extra — o container canônico do portal
          (DashboardLayout: max-w-5xl mx-auto + p-4 md:p-8) já dá o espaçamento,
          igual à Show. O px-4 py-6 extra fazia a ficha/tabs SALTAR ao trocar de
          aba (SZ-1588). Org-level segue com o próprio padding. */}
      {scope_worker_header ? (
        <WorkerPortalShell worker={scope_worker_header} section="credenciais">{body}</WorkerPortalShell>
      ) : (
        <div className="px-4 py-6 space-y-6">{body}</div>
      )}

      {/* Org-level (sidebar): criação seca da credencial da organização. */}
      <CredentialFormModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        postUrl="/dashboard/credentials"
      />

      {/* Portal do agente: conectar = reaproveitar da org OU criar nova já
          anexada a este agente. */}
      {scope_worker && (
        <ConnectCredentialModal
          open={connectModalOpen}
          onClose={() => setConnectModalOpen(false)}
          workerId={scope_worker.id}
          workerName={workerFirstName}
          attachableCredentials={attachable_credentials}
        />
      )}
    </>
  );
};

CredentialsIndex.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>;

export default CredentialsIndex;
